/**
 * Kasse: Gerät anmelden → Wer kassiert (PIN) → Warenkorb → Fertig/Wartet → Bezahlt | Mehrdeutig | Nicht gefunden.
 * Läuft auf Tablet (zweispaltig) und Handy (gestapelt). Live-Status über WebSocket, Fallback Polling.
 */
import { Marke } from "../ui/Marke";
import { Dialog } from "../ui";
import { Ladenplan } from "../ui/Ladenplan";
import { useKassePlan, useKasseAufgaben, useKasseAufgabeErledigt } from "./api";

/** „Wo steht Regal 055?“ – Plan ohne Mieterdaten, nur wenn der Laden einen hat. */
/** E10.2: Offene Aufgaben für die Schicht – abhaken direkt an der Kasse. */
function AufgabenKnopf() {
  const [offen, setOffen] = useState(false);
  const q = useKasseAufgaben(true);
  const erledigt = useKasseAufgabeErledigt();
  const n = q.data?.aufgaben.length ?? 0;
  if (!n && !offen) return null;
  return (
    <>
      <Button variant="quiet" icon="check" onClick={() => setOffen(true)}>Aufgaben{n ? ` (${n})` : ""}</Button>
      <Dialog open={offen} title="Aufgaben für die Schicht" size="md" onClose={() => setOffen(false)} actions={<Button variant="secondary" onClick={() => setOffen(false)}>Schließen</Button>}>
        {n === 0 ? <p className="sc-muted">Nichts offen.</p> : (
          <div className="sc-col" style={{ gap: 6 }}>
            {(q.data?.aufgaben ?? []).map((a) => (
              <label key={a.id} className="sc-check" style={{ alignItems: "flex-start" }}>
                <input type="checkbox" checked={false} onChange={() => erledigt.mutate(a.id)} />
                <span><span className="sc-strong">{a.titel}</span>{a.text ? <div className="sc-muted sc-small">{a.text}</div> : null}{a.faelligTag ? <div className="sc-muted sc-small">fällig {a.faelligTag.slice(8, 10)}.{a.faelligTag.slice(5, 7)}.{a.zugewiesenName ? ` · ${a.zugewiesenName}` : ""}</div> : a.zugewiesenName ? <div className="sc-muted sc-small">{a.zugewiesenName}</div> : null}</span>
              </label>
            ))}
          </div>
        )}
      </Dialog>
    </>
  );
}

function PlanKnopf() {
  const [offen, setOffen] = useState(false);
  const plan = useKassePlan(true);
  if (!plan.data || !plan.data.regale.some((r) => r.platz)) return null;
  const p = plan.data;
  return (
    <>
      <Button variant="quiet" icon="library" onClick={() => setOffen(true)}>Ladenplan</Button>
      <Dialog open={offen} title="Ladenplan" onClose={() => setOffen(false)} actions={<Button variant="secondary" onClick={() => setOffen(false)}>Schließen</Button>}>
        <Ladenplan spalten={p.plan.spalten} zeilen={p.plan.zeilen} elemente={p.plan.elemente} regale={p.regale} hintergrundUrl={p.plan.hintergrundKey ? "/api/kasse/plan/hintergrund" : null} maxHoehe={520} />
      </Dialog>
    </>
  );
}
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Notice, Skeleton, StatusBadge } from "../ui";
import { useIch } from "../api/ich";
import { ApiFehler } from "../api/client";
import { useGeraet, useNeuerWarenkorb, useSperren, useWarenkorb, type GeraetDaten } from "./api";
import { Station } from "./Station";
import { useLiveVerbindung } from "./ws";
import { GeraetAnmelden } from "./GeraetAnmelden";
import { WerKassiert } from "./WerKassiert";
import { WarenkorbAnsicht } from "./Warenkorb";
import { Bezahlt, Mehrdeutig, NichtGefunden, Wartet } from "./Zahlung";

export function KasseApp() {
  const ich = useIch();
  const geraet = useGeraet();

  if (ich.isPending || geraet.isPending) {
    return (
      <div className="app-zustand">
        <Skeleton lines={3} />
      </div>
    );
  }
  // Kein Gerät angemeldet: Web-Sitzung mit Mitgliedschaft nötig, sonst zur Anmeldung
  if (!geraet.data?.geraet) {
    if (!ich.data?.angemeldet) {
      window.location.assign("/anmelden?ziel=kasse");
      return null;
    }
    if (!ich.data.rolle) {
      return (
        <div className="app-public">
          <div className="app-public__card">
            <Notice tone="attention" title="Kein Zugang zur Kasse">Dieses Konto gehört zu keinem Laden als Personal. Bitte mit dem Konto des Inhabers oder eines Mitarbeiters anmelden.</Notice>
          </div>
        </div>
      );
    }
    return <GeraetAnmelden />;
  }
  if (window.location.pathname.replace(/\/$/, "") === "/kasse/station") return <div className="kasse sc-root"><Station ladenName={geraet.data.laden?.name ?? ""} /></div>;
  return <KasseGeraet daten={geraet.data} />;
}

function KasseGeraet({ daten }: { daten: GeraetDaten }) {
  const qc = useQueryClient();
  const sperren = useSperren();
  const aktiv = !!daten.kassierer;
  const verbunden = useLiveVerbindung(aktiv);
  const warenkorb = useWarenkorb(aktiv);
  const neu = useNeuerWarenkorb();
  const sperreMs = (daten.einstellungen?.sperreNachMinuten ?? 5) * 60_000;
  const letzteAktion = useRef(Date.now());

  // Kassensperre nach Inaktivität
  useEffect(() => {
    if (!aktiv) return;
    const merke = () => (letzteAktion.current = Date.now());
    const events = ["pointerdown", "keydown", "touchstart"] as const;
    events.forEach((e) => window.addEventListener(e, merke, { passive: true }));
    const t = setInterval(() => {
      if (Date.now() - letzteAktion.current > sperreMs) sperren.mutate();
    }, 10_000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, merke));
      clearInterval(t);
    };
  }, [aktiv, sperreMs]);

  const hub = daten.hub;
  const sumupBadge = !daten.laden?.sumupVerbunden ? (
    <StatusBadge status="neutral">SumUp nicht verbunden</StatusBadge>
  ) : hub?.sumup === "getrennt" ? (
    <StatusBadge status="error">SumUp getrennt</StatusBadge>
  ) : hub?.sumup === "pause" ? (
    <StatusBadge status="attention">SumUp wartet (429)</StatusBadge>
  ) : (
    <StatusBadge status="paid">SumUp verbunden</StatusBadge>
  );

  return (
    <div className="kasse sc-root">
      <header className="sc-bar">
        <div className="sc-row">
          {daten.laden?.logo ? <img src={`/api/public/laden/${daten.laden.slug}/logo`} alt="" className="app-logo" width={40} height={40} /> : <Marke size={40} />}
          <div className="sc-col" style={{ gap: 0 }}>
            <span className="sc-bar__title">{daten.laden?.name}</span>
            <span className="sc-muted sc-small">
              {daten.geraet?.name}
              {daten.kassierer ? ` · ${daten.kassierer.name}` : " · nicht angemeldet"}
              {aktiv && !verbunden ? " · Live-Verbindung getrennt" : ""}
            </span>
          </div>
        </div>
        <div className="sc-row">
          <PlanKnopf />
          <AufgabenKnopf />
          {aktiv && warenkorb.data?.warenkorb && warenkorb.data.warenkorb.status === "offen" && warenkorb.data.warenkorb.positionen.length === 0 ? (
            <Button variant="quiet" icon={warenkorb.data.warenkorb.uebung ? "x" : "circle-help"} loading={neu.isPending} onClick={() => neu.mutate({ uebung: !warenkorb.data?.warenkorb.uebung })}>{warenkorb.data.warenkorb.uebung ? "Übung beenden" : "Übungsmodus"}</Button>
          ) : null}
          {daten.displayCode ? <a href={`/display/${daten.displayCode}`} target="_blank" rel="noreferrer" className="rg-btn rg-btn--quiet">Kundendisplay</a> : null}
          {daten.event ? <StatusBadge status="paid">{daten.event.name}{daten.event.rabattProzent ? ` · ${daten.event.rabattProzent} % auf alles` : ""}{daten.event.provisionProzent !== null ? ` · Provision ${daten.event.provisionProzent} %` : ""}</StatusBadge> : null}
          {sumupBadge}
          {aktiv ? (
            <Button variant="quiet" icon="users" onClick={() => sperren.mutate()} loading={sperren.isPending}>
              Mitarbeiter wechseln
            </Button>
          ) : null}
        </div>
      </header>
      {!aktiv ? (
        <WerKassiert daten={daten} />
      ) : warenkorb.isPending ? (
        <div className="app-zustand">
          <Skeleton lines={3} thumb />
        </div>
      ) : warenkorb.isError ? (
        <div className="app-zustand">
          <Notice tone="error" title="Warenkorb konnte nicht geladen werden" action={<Button variant="secondary" icon="undo-2" onClick={() => void warenkorb.refetch()}>Erneut versuchen</Button>}>
            {warenkorb.error instanceof ApiFehler ? warenkorb.error.message : "Bitte die Verbindung prüfen."}
          </Notice>
        </div>
      ) : (
        <WarenkorbRouter daten={daten} antwort={warenkorb.data} neuLaden={() => void qc.invalidateQueries({ queryKey: ["kasse"] })} />
      )}
    </div>
  );
}

function WarenkorbRouter({ daten, antwort, neuLaden }: { daten: GeraetDaten; antwort: NonNullable<ReturnType<typeof useWarenkorb>["data"]>; neuLaden: () => void }) {
  const [keinePasst, setKeinePasst] = useState(false);
  const wk = antwort.warenkorb;
  useEffect(() => setKeinePasst(false), [wk.id, wk.status]);
  switch (wk.status) {
    case "offen":
      return <WarenkorbAnsicht daten={daten} warenkorb={wk} />;
    case "wartet_auf_zahlung":
      return <Wartet warenkorb={wk} sumupVerbunden={!!daten.laden?.sumupVerbunden} hub={daten.hub} />;
    case "bezahlt":
    case "abgebrochen":
      return <Bezahlt warenkorb={wk} />;
    case "mehrdeutig":
      return keinePasst ? <NichtGefunden warenkorb={wk} kandidaten={antwort.kandidaten} zurueck={() => setKeinePasst(false)} /> : <Mehrdeutig warenkorb={wk} kandidaten={antwort.kandidaten} keinePasst={() => setKeinePasst(true)} />;
    case "nicht_gefunden":
      return <NichtGefunden warenkorb={wk} kandidaten={antwort.kandidaten} />;
    default:
      return (
        <div className="app-zustand">
          <Notice tone="error" title="Unbekannter Zustand" action={<Button onClick={neuLaden}>Neu laden</Button>} />
        </div>
      );
  }
}
